import { componentCls } from "@faire/slate/lib/tracking";
import { AccordionChevron } from "@faire/slate/private/accordion/AccordionChevron";
import { AccordionItemBase } from "@faire/slate/private/accordion/AccordionItemBase";
import { AccordionItemContent } from "@faire/slate/private/accordion/AccordionItemContent";
import { AccordionItemSummary } from "@faire/slate/private/accordion/AccordionItemSummary";
import { IAccordionItemProps } from "@faire/slate/private/accordion/types";
import React from "react";

type IProps = {
  /**
   * Controls the open state of the accordion item.
   */
  expanded?: boolean;

  /**
   * The label of the accordion item.
   */
  label: string;

  /**
   * The content of the accordion item. When content is a string, it will be
   * rendered as a paragraph, otherwise it will be rendered as-is.
   */
  content: string | React.ReactNode;

  /**
   * **web**
   * Event handler for the toggle event.
   * @ref https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/toggle_event
   */
  onToggle?: React.ToggleEventHandler<HTMLDetailsElement>;

  // DO NOT ADD any new prop without aligning with Slate team first
} & IAccordionItemProps;

/**
 * An accordion item is a single section of an accordion group.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * @trackcomponent
 * @version 2.0.0
 */
export const AccordionItem = ({
  content,
  expanded,
  label,
  ...props
}: IProps) => {
  return (
    <AccordionItemBase {...props} className={componentCls()} open={expanded}>
      <AccordionItemSummary>
        {label}
        <AccordionChevron />
      </AccordionItemSummary>
      <AccordionItemContent>
        {typeof content === "string" ? <p>{content}</p> : content}
      </AccordionItemContent>
    </AccordionItemBase>
  );
};
